import React from "react";
import { CheckCircle2, XCircle, AlertTriangle, Phone, Mail, Globe, FileText } from "lucide-react";
import { calculateProfileCompleteness, countPromotionChannels } from "../../utils/profileCompleteness";

const BasicValidationChecks = ({ promoter }) => {
  if (!promoter) return null;

  const profileCompletion = calculateProfileCompleteness(promoter);
  const channelCount = countPromotionChannels(promoter.promotion_channels);

  const phoneVerified = !!promoter.phone_number_verified_at;
  const emailVerified = !!promoter.email_verified_at;
  const atLeastOneVerified = phoneVerified || emailVerified;

  const checks = [
    {
      id: "contact_verified",
      label: "Contact Verification",
      description: "At least one contact method verified (phone OR email)",
      icon: phoneVerified && emailVerified ? Mail : phoneVerified ? Phone : Mail,
      passed: atLeastOneVerified,
      critical: true,
      details: phoneVerified && emailVerified
        ? "Both phone and email verified ✓"
        : phoneVerified
        ? `Phone verified on ${new Date(promoter.phone_number_verified_at).toLocaleDateString()}`
        : emailVerified
        ? `Email verified on ${new Date(promoter.email_verified_at).toLocaleDateString()}`
        : "Neither phone nor email verified",
      subDetails: phoneVerified && emailVerified ? [
        { label: "Phone", verified: true, date: promoter.phone_number_verified_at },
        { label: "Email", verified: true, date: promoter.email_verified_at }
      ] : phoneVerified && !emailVerified ? [
        { label: "Phone", verified: true, date: promoter.phone_number_verified_at },
        { label: "Email", verified: false }
      ] : !phoneVerified && emailVerified ? [
        { label: "Phone", verified: false },
        { label: "Email", verified: true, date: promoter.email_verified_at }
      ] : null,
    },
    {
      id: "channels_declared",
      label: "Promotion Channels Declared",
      description: "At least one promotion channel",
      icon: Globe,
      passed: channelCount > 0,
      critical: true,
      details: channelCount > 0
        ? `${channelCount} channel${channelCount !== 1 ? "s" : ""} declared`
        : "No promotion channels declared",
    },
    {
      id: "profile_completeness",
      label: "Profile Completeness",
      description: "Minimum 80% profile completion",
      icon: FileText,
      passed: profileCompletion.percentage >= 80,
      critical: true,
      details: `${profileCompletion.percentage}% complete (${profileCompletion.completedCount}/${profileCompletion.total} fields)`,
    },
  ];

  const allPassed = checks.every((check) => check.passed);
  const failedCount = checks.filter((check) => !check.passed).length;

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex gap-3 justify-between items-start pb-4 mb-6 border-b border-gray-100">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Basic Validation</h3>
          <p className="mt-1 text-sm text-gray-500">
            Automated checks - all must pass to proceed
          </p>
        </div>
        {allPassed ? (
          <div className="flex gap-2 items-center px-3 py-1.5 text-green-700 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-semibold">Passed</span>
          </div>
        ) : (
          <div className="flex gap-2 items-center px-3 py-1.5 text-red-700 bg-red-50 rounded-lg border border-red-200">
            <XCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">{failedCount} Failed</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {checks.map((check) => {
          const Icon = check.icon;
          return (
            <div
              key={check.id}
              className={`p-4 rounded-lg border transition-all ${
                check.passed
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <div className="flex gap-3 items-start">
                <div
                  className={`p-2 rounded-lg ${
                    check.passed ? "bg-green-100" : "bg-red-100"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      check.passed ? "text-green-600" : "text-red-600"
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex gap-2 items-center mb-1">
                    <h4 className="font-semibold text-gray-900">{check.label}</h4>
                    {check.critical && (
                      <span className="px-2 py-0.5 text-xs font-medium text-red-700 bg-red-100 rounded">
                        Critical
                      </span>
                    )}
                  </div>
                  <p className="mb-2 text-sm text-gray-600">{check.description}</p>
                  <div
                    className={`p-2 rounded text-xs font-medium ${
                      check.passed
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {check.details}
                  </div>
                  {check.subDetails && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {check.subDetails.map((detail, idx) => (
                        <div
                          key={idx}
                          className={`p-2 rounded text-xs ${
                            detail.verified
                              ? "bg-green-50 border border-green-200"
                              : "bg-gray-50 border border-gray-200"
                          }`}
                        >
                          <div className="flex gap-1 items-center">
                            {detail.verified ? (
                              <CheckCircle2 className="w-3 h-3 text-green-600" />
                            ) : (
                              <XCircle className="w-3 h-3 text-gray-400" />
                            )}
                            <span className={detail.verified ? "text-green-800 font-medium" : "text-gray-600"}>
                              {detail.label}
                            </span>
                          </div>
                          {detail.verified && detail.date && (
                            <div className="mt-1 text-xs text-green-700">
                              {new Date(detail.date).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {check.passed ? (
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!allPassed && (
        <div className="flex gap-3 items-start p-4 mt-4 bg-red-50 rounded-lg border-2 border-red-200">
          <AlertTriangle className="flex-shrink-0 w-5 h-5 text-red-600" />
          <div>
            <h4 className="mb-1 font-semibold text-red-900">Automatic Rejection Required</h4>
            <p className="text-sm text-red-800">
              This promoter has failed {failedCount} critical validation check{failedCount !== 1 ? "s" : ""}. 
              According to policy, failure at this stage results in automatic rejection. 
              Please reject this application and inform the promoter of the missing requirements.
            </p>
          </div>
        </div>
      )}

      {allPassed && profileCompletion.percentage < 100 && (
        <div className="p-4 mt-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="mb-2 text-sm font-semibold text-blue-900">Profile Completion Details</h4>
          <div className="text-xs text-blue-800">
            <p className="mb-1">
              <strong>Completed:</strong> {profileCompletion.completedCount} fields
            </p>
            <p>
              <strong>Missing:</strong> {profileCompletion.missingCount} optional fields
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicValidationChecks;
